import {useCallback, useState, useMemo, useEffect, useRef} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {
  AgentApprovalResponse,
  AgentType,
  ChatThreadStatus,
  LLMRole,
  RoutineRunAs,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatSessionQuery} from 'llm/chat-library/__generated/ChatSessionQuery';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {
  AgentApprovalUpdateSource,
  buildAgentApprovalDecision,
  ChatReadTrigger,
  markChatRead,
  updateChatAgentApproval,
  updateChatAgentWorkspaceFolder,
} from 'llm/chat-library/ChatDAO';
import {
  ChatDraftStatus,
  clearDraftMessageContent,
  getDraftState,
  seedDraftStateFromMessage,
  useChatDraftState,
} from 'llm/chat-library/ChatDraftState';
import {getCompletedUploads} from 'llm/chat-library/ChatFileHelper';
import {notifyChatMarkedRead} from 'llm/chat-library/ChatReadStateEvents';
import {
  getLatestApplicationState,
  getLatestCitationGenerationMode,
  getLatestEffort,
  getLatestPublicDataEnabled,
  getLatestSystemState,
  hasChatScopedWriteApproval,
  hasFullAccessForDisplay,
} from 'llm/chat-library/ChatStateUtil';
import {useChatUnreadTrackingEnabled} from 'llm/chat-library/ChatUnreadTrackingContext';
import {getAgentWorkspaceFolderId} from 'llm/chat-library/getAgentWorkspaceFolderId';
import {MessageCreator} from 'llm/chat-library/messages/ChatMessageSender';
import {getChatMessageSenderContext} from 'llm/chat-library/messages/ChatMessageSenderContext';
import {
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from 'llm/chat-library/messages/MessageTypes';
import {
  createPendingUserMessage,
  createProgressMessage,
  createStreamingMessage,
  isApprovalRequestMessage,
} from 'llm/chat-library/messages/MessageUtils';
import {hasAcknowledgedInterruptEvent} from 'llm/chat-library/progress/InterruptEventUtils';
import {
  getEffectiveInterruptProgressPhase,
  InterruptRequestState,
  isInterruptStopInProgress,
  withInterruptProgress,
} from 'llm/chat-library/progress/InterruptProgress';
import {useToolProgressRealtime} from 'llm/chat-library/progress/optimisticToolResults';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {
  ToolCallsByCompletedProgressBoundary,
  getAllToolCalls,
  getProgressToolCalls,
  getCurrentExchangeProgressData,
  getToolCallsByCompletedProgressBoundary,
} from 'llm/chat-library/progress/ProgressUtils';
import {getDefaultAgentType} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import useCompleteSkillOnboardingFromChat from 'llm/chat-library/skills/useCompleteSkillOnboardingFromChat';
import {
  isCitationGenerationToolCall,
  RESPONSE_TOOLS,
  TOOL_DISPLAY_NAMES_IN_PROGRESS,
  ToolType,
} from 'llm/chat-library/tool-details/types';
import {
  ChatSessionNode,
  ChatSessionSidebarNode,
  useChatSessionEvents,
} from 'llm/chat-library/useChatSessionEvents';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {isRoutineRunAsDisabled} from 'llm/routines/routineAuthorization';
import {useIsRoutineServiceAccountUiEnabled} from 'llm/routines/routineServiceAccountRollout';
import * as Perf from 'performance/Perf';
import {RealtimeMessageType} from 'realtime/Constants';
import {RealtimeMessage} from 'realtime/RealtimeChannel';
import {useRealtimeRefetch} from 'realtime/RealtimeUpdateSubscriptionWrapper';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';

function isPageVisibleAndFocused(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

type AllThreadLlmEvent = Extract<
  ChatSessionQuery['chatSession'],
  {__typename: 'ChatSessionNode'}
>['allThreads'][number]['events'][number];

function getAllThreadLlmEvents(
  thread: Extract<ChatSessionQuery['chatSession'], {__typename: 'ChatSessionNode'}>['allThreads'][number]
): Array<AllThreadLlmEvent> {
  return thread.events;
}

interface PendingResponseToolCall {
  toolId: string;
  answer: string;
  args: Record<string, unknown>;
  toolName: ToolType;
  createdAt: string;
}

function getPendingResponseToolCall(
  chatSessionQueryData: ChatSessionQuery | undefined
): PendingResponseToolCall | null {
  if (chatSessionQueryData?.chatSession.__typename !== 'ChatSessionNode') {
    return null;
  }

  const rootThread = chatSessionQueryData.chatSession.threads[0];
  const displayEvents = rootThread?.events ?? [];
  const lastDisplayEvent = displayEvents.findLast(
    (event: (typeof displayEvents)[number]): event is DisplayMessageChatEventNode =>
      event.__typename === 'DisplayMessageChatEventNode'
  );
  if (!lastDisplayEvent || lastDisplayEvent.role !== LLMRole.USER) {
    return null;
  }

  const pendingToolResultIds = new Set<string>();
  const candidateResponseToolCalls: Array<PendingResponseToolCall> = [];
  for (const thread of chatSessionQueryData.chatSession.allThreads) {
    for (const event of getAllThreadLlmEvents(thread)) {
      if (event.__typename !== 'LLMMessageChatEventNode') {
        continue;
      }
      if (event.createdAt < lastDisplayEvent.createdAt) {
        continue;
      }
      for (const {content} of event.messageBlocks) {
        if (content.type === 'tool_result') {
          pendingToolResultIds.add(content.value.id);
          continue;
        }
        if (content.type !== 'tool_call') {
          continue;
        }
        if (!RESPONSE_TOOLS.includes(content.value.name)) {
          continue;
        }
        const answer = content.value.args.answer;
        if (typeof answer !== 'string' || answer.length === 0) {
          continue;
        }
        candidateResponseToolCalls.push({
          toolId: content.value.id,
          answer,
          args: content.value.args,
          toolName: content.value.name,
          createdAt: event.createdAt,
        });
      }
    }
  }

  for (let i = candidateResponseToolCalls.length - 1; i >= 0; i--) {
    const toolCall = candidateResponseToolCalls[i];
    if (!pendingToolResultIds.has(toolCall.toolId)) {
      return toolCall;
    }
  }

  return null;
}

function getResponseToolDisplayType(toolName: string): string | null {
  return toolName === ToolType.CompleteAnswer ? 'report' : null;
}

function getResponseToolProgressMessage(
  toolCall: PendingResponseToolCall,
  userDisplayEvent: DisplayMessageChatEventNode,
  isInterrupting: boolean
): string | null {
  const isGeneratingCitationReport = isCitationGenerationToolCall(
    {name: toolCall.toolName, args: toolCall.args},
    userDisplayEvent
  );
  if (isGeneratingCitationReport && isInterrupting) {
    return 'Stopping citation generation...';
  }
  const displayName =
    toolCall.toolName === ToolType.CompleteAnswer && !isGeneratingCitationReport
      ? TOOL_DISPLAY_NAMES_IN_PROGRESS[ToolType.Respond]
      : TOOL_DISPLAY_NAMES_IN_PROGRESS[toolCall.toolName];
  return displayName == null ? null : `${displayName}...`;
}

function getActiveProgressStatus(
  threadStatus: string | null,
  hasToolCalls: boolean,
  effectiveInterruptProgressPhase: ReturnType<typeof getEffectiveInterruptProgressPhase>
): ProgressStatus {
  if (threadStatus === ChatThreadStatus.QUEUED) {
    return ProgressStatus.QUEUED;
  }
  if (
    hasToolCalls ||
    effectiveInterruptProgressPhase === 'requesting' ||
    effectiveInterruptProgressPhase === 'attempting'
  ) {
    return ProgressStatus.IN_PROGRESS;
  }
  return ProgressStatus.STARTED;
}

export interface SendMessageParams {
  /** When true, archives previous failed messages before sending */
  retry?: boolean;
}

export interface UseMessageThreadOptions {
  chatSessionId: string;
  /**
   * When true, fetches tool call data from allThreads and derives progressData.
   * Enables activity/progress tracking UI for agents that use tools.
   */
  includeToolCalls?: boolean;
  /**
   * When true, disables realtime update subscriptions.
   * Used for read-only/locked chats where the user shouldn't receive live updates.
   */
  disableRealtimeUpdates?: boolean;
  /** Whether to load the canonical right-sidebar collections with the chat session. */
  includeSidebarData?: boolean;
  /**
   * When true, completed progress sections render expanded and tool-call appendix content is included.
   * Used for chat PDF export / print view.
   */
  viewerMode?: boolean;
}

export interface RoutineBackedChatOrigin {
  isRoutineBacked: boolean;
  runsAsServiceAccount: boolean;
  isRoutineDisabled: boolean;
  isResolved: boolean;
}

function getRoutineBackedChatOrigin(chatSession: ChatSessionNode | undefined): RoutineBackedChatOrigin {
  if (chatSession == null) {
    return {
      isRoutineBacked: false,
      runsAsServiceAccount: false,
      isRoutineDisabled: false,
      isResolved: false,
    };
  }
  const routineExecution = chatSession.routineExecution;
  if (routineExecution == null) {
    return {
      isRoutineBacked: false,
      runsAsServiceAccount: false,
      isRoutineDisabled: false,
      isResolved: true,
    };
  }
  if (routineExecution.__typename !== 'RoutineExecutionNode') {
    return {
      isRoutineBacked: true,
      runsAsServiceAccount: false,
      isRoutineDisabled: false,
      isResolved: false,
    };
  }
  const runAs = routineExecution.routine.runAs;
  return {
    isRoutineBacked: true,
    runsAsServiceAccount: runAs === RoutineRunAs.SERVICE_ACCOUNT,
    isRoutineDisabled: isRoutineRunAsDisabled(runAs),
    isResolved: true,
  };
}

export interface ChatSidebarData {
  chatSessionId: string;
  /**
   * Objects the chat touched or produced, as one list. Each entry's artifactKind distinguishes
   * plain affected objects (null) from artifacts the chat produced.
   */
  affectedObjects: ChatSessionSidebarNode['affectedObjects'];
  inputFiles: ChatSessionSidebarNode['inputFiles'];
}

export interface UseMessageThreadResult {
  /** Display events including optimistic user messages, streaming messages, and completed thinking sections */
  events: Array<ClientDisplayChatEvent>;
  /** Displayable progress data from the session. Null if includeToolCalls is false or no data available. */
  progressData: ProgressData | null;
  /** Unfiltered tool-call data used to resolve citations that reference hidden-but-citable tools. */
  citationData: ProgressData | null;
  /** Latest application state from StateUpdateChatEventNode events, derived from raw server events */
  latestApplicationState: object | null;
  /** Canonical session inputs, artifacts, and affected objects for the right sidebar. */
  sidebarData: ChatSidebarData | null;
  /** Whether this chat currently has session-scoped write approval. */
  fullAccessEnabled: boolean;
  isLoading: boolean;
  /** True when assistant is processing (thread status ACTIVE/QUEUED or pending message exists) */
  isAssistantRunning: boolean;
  /** True when submit failed before the agent began processing and the optimistic message is still shown. */
  isSubmitErrorMessage: boolean;
  /**
   * True when the agent has paused and is waiting for structured user input before it can continue.
   * Thread status is AWAITING_USER_INPUT. The agent is NOT running — the user must act.
   * Consumers should read latestApplicationState to get the structured data (e.g. onboarding questions).
   */
  isAwaitingUserInput: boolean;
  /**
   * True when the thread has finished successfully. Thread status is COMPLETED.
   * No further user input or agent activity is expected.
   */
  isCompleted: boolean;
  errorMessage: string | null;
  /**
   * True when errorMessage comes from a thread ERROR status (as opposed to a query/network error).
   * In this case the error is already rendered as a progress message and input should remain enabled.
   */
  isThreadError: boolean;
  /** True when the thread is in an error state and the last display message is from the user. */
  isLastMessageFromUser: boolean;
  sendMessage: (params?: SendMessageParams) => void;
  /**
   * True when Stop can be sent: thread is ACTIVE/QUEUED, the latest persisted display
   * message is from the user, and there is no optimistic pending send in flight.
   */
  canInterruptAssistant: boolean;
  /** True while the agent is still stopping (interrupt HTTP in flight or thread still ACTIVE/QUEUED). */
  isInterrupting: boolean;
  interruptAssistant: () => Promise<void>;
  grantFullAccess: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  revokeFullAccess: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  /** Set (or recover) the agent workspace folder for this chat. See ChatDAO.updateChatAgentWorkspaceFolder. */
  updateAgentWorkspaceFolder: (agentWorkspaceFolderId: string) => Promise<boolean>;
  refetch: () => Promise<unknown>;
  /** Session display name from the server, when the chat session query has loaded. */
  chatSessionDisplayName: string | null;
  /** Whether WRITE_ACTIONS is granted for the rest of the chat. */
  hasChatScopedWriteApproval: boolean;
  /** Routine execution metadata for message sender labels; null when not a routine-backed chat. */
  messageSenderContext: ReturnType<typeof getChatMessageSenderContext>;
  /** Chat session creator; used as sender fallback when the routine runs as creator. */
  sessionCreatorFallback: MessageCreator | null;
  /** Whether the chat is backed by a routine and which identity that routine uses. */
  routineOrigin: RoutineBackedChatOrigin;
}

/**
 * Hook for managing chat message threads with realtime updates and streaming support.
 *
 * This hook provides:
 * - GraphQL polling (30s interval) as a fallback for data fetching
 * - Realtime-triggered refetch when backend publishes `llm_chat_update`
 * - Streaming message support for ASK (`llm_response_chunk`, `llm_response_complete`)
 * - Optimistic rendering of user messages
 * - Progress data derivation when `includeToolCalls` is enabled
 * - Completed thinking sections inserted before assistant messages
 *
 * NOTE: For optimal UX, the backend should call `LLMChatControllerSQLA.commit_and_publish_chat_update`
 * after significant state changes (tool calls, status changes, etc.) to trigger immediate refetches
 * rather than waiting for the 30s polling interval.
 */
export function useMessageThread({
  chatSessionId,
  includeToolCalls = false,
  disableRealtimeUpdates = false,
  includeSidebarData = false,
  viewerMode = false,
}: UseMessageThreadOptions): UseMessageThreadResult {
  const isRoutineServiceAccountUiEnabled = useIsRoutineServiceAccountUiEnabled();
  const chatUnreadTrackingEnabled = useChatUnreadTrackingEnabled();
  const askFallbackAgentType = BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT
    ? AgentType.ASSISTANT
    : AgentType.ASK;
  // ============================================
  // 0. MCP Server IDs
  // ============================================
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const mcpServerIds = mcpEnabledServers?.enabledServerIds;
  // Undefined until the enabled-servers query succeeds; ordinary messages sent before then leave the
  // chat's saved selection unchanged. excludeMcpServers overrides both saved and fallback selections.
  const [interruptRequestState, setInterruptRequestState] = useState(InterruptRequestState.NOT_REQUESTED);
  const interruptRequestInFlightRef = useRef(false);
  const pendingReadTriggerRef = useRef<ChatReadTrigger | null>(ChatReadTrigger.OPENED_CHAT);
  const markReadInFlightRef = useRef(false);
  const pageFocusedRef = useRef(isPageVisibleAndFocused());
  const unreadTrackingChatSessionIdRef = useRef(chatSessionId);
  const [readCheckRevision, setReadCheckRevision] = useState(0);
  unreadTrackingChatSessionIdRef.current = chatSessionId;

  useEffect(() => {
    pendingReadTriggerRef.current = chatUnreadTrackingEnabled ? ChatReadTrigger.OPENED_CHAT : null;
    markReadInFlightRef.current = false;
  }, [chatSessionId, chatUnreadTrackingEnabled]);

  // ============================================
  // 1. Pending User Message (optimistic rendering)
  // ============================================
  // Derive pending user message from draft state when status is PENDING.
  // This provides optimistic rendering while the message is being sent to the server.
  // The replyToId and retryOfId fields are used to:
  // - Clear the optimistic message when its persisted version appears (detected by comparing replyToId)
  // - Filter out the original message when showing a retry (using retryOfId)
  const [draftState] = useChatDraftState(chatSessionId);

  const pendingUserMessage: ClientOptimisticUserMessage | null = useMemo(() => {
    // When status is PENDING, we're sending a message - show optimistic UI
    if (draftState?.status === ChatDraftStatus.PENDING) {
      return createPendingUserMessage({
        text: draftState.message.text,
        fileUploads: getCompletedUploads(draftState.message.selectedFiles),
        agentType: draftState.settings?.agentType ?? getDefaultAgentType() ?? askFallbackAgentType,
        replyToId: draftState.replyToId ?? null,
        retryOfId: draftState.retryOfId ?? null,
      });
    }
    return null;
  }, [draftState, askFallbackAgentType]);

  const hasAgentTypeError = draftState.agentTypeError != null;
  const failedUserMessage: ClientOptimisticUserMessage | null = useMemo(() => {
    if (draftState.failedUserMessage == null) {
      return null;
    }
    return createPendingUserMessage({
      text: draftState.failedUserMessage.text,
      fileUploads: getCompletedUploads(draftState.failedUserMessage.selectedFiles),
      agentType: draftState.settings?.agentType ?? getDefaultAgentType() ?? askFallbackAgentType,
      replyToId: draftState.replyToId ?? null,
      retryOfId: draftState.retryOfId ?? null,
    });
  }, [draftState, askFallbackAgentType]);

  // ============================================
  // 2. Streaming State
  // ============================================
  const [streamingMessage, setStreamingMessage] = useState<ClientStreamingAssistantMessage | null>(null);
  const {
    queuedToolCallIds,
    runningToolCallIds,
    optimisticToolResultsById,
    hasReceivedSnapshot,
    hasExplicitQueuedToolCallIds,
    onToolProgressRealtimeMessage,
  } = useToolProgressRealtime(chatSessionId);
  const receivedChunks = useRef<Map<number, string>>(new Map());
  const activeStreamId = useRef<string | null>(null);
  const closedStreamIds = useRef<Set<string>>(new Set());
  const timeToFirstTokenPerfId = useRef<Perf.PerfId | null>(null);

  // ============================================
  // 3. Server Events via GraphQL
  // ============================================
  // The replyToId field on the pending message (stored in draft state) tells us the last display
  // message ID (user or assistant) at the time the user sent their message. We use this to detect
  // when the optimistic message has been persisted: when we see a user message that follows the
  // replyToId position, we know the new message has been persisted and can clear the draft.
  //
  // This approach is robust because:
  // - The replyToId is stored in the draft state (redux-like), not a ref, so it has the same
  //   lifecycle as the optimistic message itself
  // - It directly answers: "has a new user message appeared after the last display message?"
  // - Agents that publish realtime updates frequently won't cause premature clearing because the replyToId comparison is stable
  const {
    events: serverEvents,
    isLoading,
    errorMessage,
    threadStatus,
    refetch,
    chatSession,
    chatSessionSidebar,
    chatSessionQueryData,
    chatType,
  } = useChatSessionEvents({
    chatSessionId,
    includeToolCalls,
    includeSidebarData,
    onEventsUpdated: (events) => {
      // Get the current draft state to check if we're waiting for an optimistic message to be persisted
      const currentDraft = getDraftState(chatSessionId);
      if (currentDraft?.status === ChatDraftStatus.PENDING) {
        const replyToId = currentDraft.replyToId ?? null;

        // Check if there's a user message that follows the replyToId.
        // replyToId points at the last DisplayMessageChatEventNode (any role) when we sent the message.
        // Using findLastIndex since relevant IDs are near the end of the thread (close to O(1) in common case).
        // Handles both the case for initial message (replyToIdIndex == -1) or follow-up (replyToIdIndex >= 0)
        const replyToIdIndex = events.findLastIndex(
          (event) =>
            replyToId != null && event.__typename === 'DisplayMessageChatEventNode' && replyToId === event.id
        );
        const hasUserMessageAfterReplyTo =
          events.findLastIndex(
            (event, idx) =>
              idx > replyToIdIndex &&
              event.__typename === 'DisplayMessageChatEventNode' &&
              event.role === LLMRole.USER
          ) !== -1;

        if (hasUserMessageAfterReplyTo) {
          clearDraftMessageContent(chatSessionId);
        }
      }

      // Clear streaming message if server has the final assistant message
      if (events.length > 0) {
        const lastDisplayEvent = events.findLast((e) => e.__typename === 'DisplayMessageChatEventNode');
        if (lastDisplayEvent && lastDisplayEvent.role === LLMRole.ASSISTANT) {
          setStreamingMessage(null);
          receivedChunks.current.clear();
        }
      }
    },
  });
  const routineOrigin = useMemo(() => getRoutineBackedChatOrigin(chatSession), [chatSession]);
  const excludeMcpServers = routineOrigin.runsAsServiceAccount;
  const omitSources = !routineOrigin.isResolved;
  const isAgentSubmitErrorMessage = failedUserMessage != null && hasAgentTypeError;

  // ============================================
  // 4. Realtime-Triggered Refetch
  // ============================================
  const isThreadActive = threadStatus === ChatThreadStatus.ACTIVE || threadStatus === ChatThreadStatus.QUEUED;
  // Note: AWAITING_USER_INPUT is intentionally excluded — the agent is paused waiting for the user.
  useRealtimeRefetch({
    apiId: chatSessionId,
    refetch,
    messageFilter: 'no_filter',
    hasIncompleteDataShouldRefetch: isThreadActive,
    disabled: disableRealtimeUpdates,
  });

  // ============================================
  // 5. Streaming Support
  // ============================================
  const handleStreamingChunk = useCallback(
    (data: {chunkNumber: number; content: string; userEventId: string; streamId?: string}) => {
      if (data.streamId != null) {
        if (closedStreamIds.current.has(data.streamId)) {
          return;
        }
        if (activeStreamId.current !== data.streamId) {
          if (activeStreamId.current != null) {
            closedStreamIds.current.add(activeStreamId.current);
          }
          activeStreamId.current = data.streamId;
          receivedChunks.current.clear();
        }
      }
      // Report time-to-first-token metric on first chunk
      if (timeToFirstTokenPerfId.current && data.chunkNumber === 0) {
        Perf.markEnd(timeToFirstTokenPerfId.current);
        Perf.stop(timeToFirstTokenPerfId.current);
        timeToFirstTokenPerfId.current = null;
      }

      receivedChunks.current.set(data.chunkNumber, data.content);

      // Check if we have all chunks in sequence
      let completeContent = '';
      let hasAllChunks = true;
      for (let i = 0; i < receivedChunks.current.size; i++) {
        if (receivedChunks.current.has(i)) {
          completeContent += receivedChunks.current.get(i);
        } else {
          hasAllChunks = false;
          break;
        }
      }

      // Only render if we have all chunks in sequence
      if (hasAllChunks) {
        setStreamingMessage(
          createStreamingMessage({
            createdAt: new Date().toISOString(),
            agentType: getDefaultAgentType() ?? askFallbackAgentType,
            responseToEventId: data.userEventId,
            streamingContent: `${completeContent}▊`,
          })
        );
      }
    },
    [askFallbackAgentType]
  );

  const handleStreamingComplete = useCallback(
    (data: {content: string; userEventId: string; chatId: string; streamId?: string; clear?: boolean}) => {
      if (data.clear) {
        // The stream is recorded as closed even if no chunk arrived yet, so later chunks for it are ignored.
        // The preview is removed only when the stream ID matches the active stream, so a clear for a
        // discarded stream does not erase the preview of a replacement stream.
        if (data.streamId != null) {
          closedStreamIds.current.add(data.streamId);
        }
        if (data.streamId == null || activeStreamId.current === data.streamId) {
          setStreamingMessage(null);
          receivedChunks.current.clear();
          activeStreamId.current = null;
        }
        return;
      }
      if (data.streamId != null && closedStreamIds.current.has(data.streamId)) {
        return;
      }
      if (data.streamId != null) {
        if (activeStreamId.current != null && activeStreamId.current !== data.streamId) {
          closedStreamIds.current.add(activeStreamId.current);
        }
        // The completed stream is marked closed so chunks that arrive after completion are ignored.
        closedStreamIds.current.add(data.streamId);
      }
      setStreamingMessage(
        createStreamingMessage({
          createdAt: new Date().toISOString(),
          agentType: getDefaultAgentType() ?? askFallbackAgentType,
          responseToEventId: data.userEventId,
          streamingContent: data.content,
        })
      );
      receivedChunks.current.clear();
      activeStreamId.current = data.streamId ?? null;

      // NB: We do not need to manually refetch messages at this point: the backend will broadcast a realtime
      // message, which will trigger a refetch via useRealtimeRefetch.
    },
    [askFallbackAgentType]
  );

  const onRealtimeMessage = useCallback(
    (message: RealtimeMessage) => {
      if (message.name === RealtimeMessageType.LLM_RESPONSE_CHUNK) {
        handleStreamingChunk(message.data);
      } else if (message.name === RealtimeMessageType.LLM_RESPONSE_COMPLETE) {
        handleStreamingComplete(message.data);
      } else if (message.name === RealtimeMessageType.TOOL_PROGRESS) {
        onToolProgressRealtimeMessage(message);
      } else if (
        message.name === RealtimeMessageType.LLM_CHAT_UPDATE &&
        chatUnreadTrackingEnabled &&
        pendingReadTriggerRef.current == null &&
        isPageVisibleAndFocused()
      ) {
        pendingReadTriggerRef.current = ChatReadTrigger.RESPONSE_ARRIVED_WHILE_FOCUSED;
      }
    },
    [chatUnreadTrackingEnabled, handleStreamingChunk, handleStreamingComplete, onToolProgressRealtimeMessage]
  );

  useRealtimeChannelSubscription(
    !disableRealtimeUpdates ? getRealtimeChannelNameForApiId(chatSessionId) : '',
    onRealtimeMessage,
    'no_filter'
  );

  // ============================================
  // 6. Assistant Running State
  // ============================================
  const isAwaitingUserInput = threadStatus === ChatThreadStatus.AWAITING_USER_INPUT;
  const isCompleted = threadStatus === ChatThreadStatus.COMPLETED;
  const latestCommittedAssistantMessageId = useMemo(
    () =>
      serverEvents.findLast(
        (event): event is DisplayMessageChatEventNode =>
          event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.ASSISTANT
      )?.id ?? null,
    [serverEvents]
  );

  useEffect(() => {
    if (!chatUnreadTrackingEnabled) {
      return;
    }
    const handleFocusChanged = (): void => {
      const isFocused = isPageVisibleAndFocused();
      if (!isFocused) {
        pageFocusedRef.current = false;
        return;
      }
      if (pageFocusedRef.current) {
        return;
      }
      pageFocusedRef.current = true;
      pendingReadTriggerRef.current = ChatReadTrigger.FOCUS_RESTORED;
      const scheduleReadCheck = (): void => {
        setReadCheckRevision((revision) => revision + 1);
      };
      refetch().then(scheduleReadCheck, scheduleReadCheck);
    };
    window.addEventListener('focus', handleFocusChanged);
    window.addEventListener('blur', handleFocusChanged);
    document.addEventListener('visibilitychange', handleFocusChanged);
    return () => {
      window.removeEventListener('focus', handleFocusChanged);
      window.removeEventListener('blur', handleFocusChanged);
      document.removeEventListener('visibilitychange', handleFocusChanged);
    };
  }, [chatUnreadTrackingEnabled, refetch]);

  useEffect(() => {
    const readTrigger = pendingReadTriggerRef.current;
    if (
      !chatUnreadTrackingEnabled ||
      !isCompleted ||
      latestCommittedAssistantMessageId == null ||
      !isPageVisibleAndFocused()
    ) {
      if (threadStatus != null && !isCompleted && readTrigger === ChatReadTrigger.OPENED_CHAT) {
        pendingReadTriggerRef.current = null;
      }
      return;
    }
    if (readTrigger == null || markReadInFlightRef.current) {
      return;
    }

    markReadInFlightRef.current = true;
    markChatRead(chatSessionId, readTrigger)
      .then(() => {
        if (
          unreadTrackingChatSessionIdRef.current === chatSessionId &&
          pendingReadTriggerRef.current === readTrigger
        ) {
          pendingReadTriggerRef.current = null;
        }
        notifyChatMarkedRead(chatSessionId);
      })
      .catch(() => {
        // Retain the trigger so the next focus or committed realtime update can retry.
      })
      .finally(() => {
        markReadInFlightRef.current = false;
      });
  }, [
    chatSessionId,
    chatUnreadTrackingEnabled,
    isCompleted,
    latestCommittedAssistantMessageId,
    readCheckRevision,
    threadStatus,
  ]);

  const interruptEventsForLastUserMessage = useMemo(() => {
    const lastUserMessage = serverEvents.findLast(
      (event): event is DisplayMessageChatEventNode =>
        event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.USER
    );
    if (lastUserMessage == null) {
      return {
        hasInterruptRequestedEvent: false,
        hasInterruptEvent: false,
      };
    }

    let hasInterruptRequestedEvent = false;
    for (let i = serverEvents.length - 1; i >= 0; i--) {
      const event = serverEvents[i];
      if (event.__typename === 'InterruptRequestChatEventNode') {
        hasInterruptRequestedEvent = true;
        break;
      }
      if (event.__typename === 'DisplayMessageChatEventNode' && event.id === lastUserMessage.id) {
        break;
      }
    }

    return {
      hasInterruptRequestedEvent,
      hasInterruptEvent: hasAcknowledgedInterruptEvent(serverEvents, lastUserMessage.id),
    };
  }, [serverEvents]);

  const activeInterruptUserMessageEventId = useMemo((): string | null => {
    if (!isThreadActive || pendingUserMessage != null || isAgentSubmitErrorMessage) {
      return null;
    }
    const lastDisplayEvent = serverEvents.findLast(
      (event): event is DisplayMessageChatEventNode => event.__typename === 'DisplayMessageChatEventNode'
    );
    if (!lastDisplayEvent || lastDisplayEvent.role !== LLMRole.USER) {
      return null;
    }
    return lastDisplayEvent.id;
  }, [isThreadActive, pendingUserMessage, isAgentSubmitErrorMessage, serverEvents]);

  const isInterruptRequestLocked =
    interruptRequestState !== InterruptRequestState.NOT_REQUESTED ||
    interruptEventsForLastUserMessage.hasInterruptRequestedEvent;
  const canInterruptAssistant = activeInterruptUserMessageEventId != null && !isInterruptRequestLocked;

  const interruptProgressInputs = useMemo(
    () => ({
      interruptRequestState,
      hasInterruptRequestedEvent: interruptEventsForLastUserMessage.hasInterruptRequestedEvent,
      hasInterruptEvent: interruptEventsForLastUserMessage.hasInterruptEvent,
      isThreadActive,
    }),
    [interruptRequestState, interruptEventsForLastUserMessage, isThreadActive]
  );

  const isAssistantRunning = isThreadActive || (pendingUserMessage != null && !isAgentSubmitErrorMessage);

  // ============================================
  // 6.5. Seed Draft State from Loaded Thread
  // ============================================
  // When the chat is loaded from the server, seed the draft state with the agent mode
  // from the last display message if it hasn't been set yet.
  // This allows Chat.tsx to use the correct agent mode for the loaded chat.
  const hasSeededDraftState = useRef(false);
  useEffect(() => {
    if (hasSeededDraftState.current || isLoading || !chatType) {
      return;
    }

    const lastDisplayEvent = serverEvents.findLast(
      (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
    );

    // Seed the draft state with settings from the last message.
    // If no messages exist, use the first available agent type for this chat type.
    const agentType = getDefaultAgentType(lastDisplayEvent?.agentType, [chatType]) ?? askFallbackAgentType;
    if (agentType) {
      seedDraftStateFromMessage(chatSessionId, {
        agentType,
        publicDataEnabled: getLatestPublicDataEnabled(serverEvents),
        citationGenerationMode: getLatestCitationGenerationMode(serverEvents),
        effort: getLatestEffort(serverEvents),
      });
    }

    hasSeededDraftState.current = true;
  }, [chatSessionId, serverEvents, isLoading, chatType, askFallbackAgentType]);

  // ============================================
  // 7. All Progress Data (for citations, activity modal)
  // ============================================
  const progressData = useMemo((): ProgressData | null => {
    if (!includeToolCalls || !chatSession) {
      return null;
    }

    const toolCalls = getProgressToolCalls(chatSession);
    if (toolCalls.length === 0) {
      return null;
    }

    return {toolCalls};
  }, [includeToolCalls, chatSession]);

  const citationData = useMemo((): ProgressData | null => {
    if (!includeToolCalls || !chatSession) {
      return null;
    }

    const toolCalls = getAllToolCalls(chatSession);
    if (toolCalls.length === 0) {
      return null;
    }

    return {toolCalls};
  }, [includeToolCalls, chatSession]);

  // ============================================
  // 8. Current Exchange Progress Data (for in-progress thinking)
  // ============================================
  const currentProgressData = useMemo((): ProgressData | null => {
    if (!includeToolCalls) {
      return null;
    }

    // When we have a pending message but no server events yet, return empty progress
    if ((pendingUserMessage || failedUserMessage) && !chatSessionQueryData) {
      return {toolCalls: []};
    }

    if (!chatSessionQueryData) {
      return null;
    }

    return getCurrentExchangeProgressData(chatSessionQueryData, serverEvents, {
      queuedToolCallIds,
      runningToolCallIds,
      optimisticToolResultsById,
      hasReceivedSnapshot,
      hasExplicitQueuedToolCallIds,
      isThreadActive,
    });
  }, [
    includeToolCalls,
    chatSessionQueryData,
    serverEvents,
    pendingUserMessage,
    failedUserMessage,
    queuedToolCallIds,
    runningToolCallIds,
    optimisticToolResultsById,
    hasReceivedSnapshot,
    hasExplicitQueuedToolCallIds,
    isThreadActive,
  ]);

  const effectiveInterruptProgressPhase = useMemo(
    () => getEffectiveInterruptProgressPhase(interruptProgressInputs),
    [interruptProgressInputs]
  );
  const isInterrupting = isInterruptStopInProgress(interruptProgressInputs);

  const currentProgressDataWithInterrupt = useMemo((): ProgressData | null => {
    if (effectiveInterruptProgressPhase == null) {
      return currentProgressData;
    }
    return withInterruptProgress(currentProgressData, effectiveInterruptProgressPhase);
  }, [currentProgressData, effectiveInterruptProgressPhase]);

  // ============================================
  // 9. Tool Calls By Assistant Message (for completed thinking sections)
  // ============================================
  const toolCallsByCompletedProgressBoundary = useMemo((): ToolCallsByCompletedProgressBoundary => {
    if (!includeToolCalls || !chatSessionQueryData) {
      return {};
    }
    return getToolCallsByCompletedProgressBoundary(chatSessionQueryData, serverEvents);
  }, [includeToolCalls, chatSessionQueryData, serverEvents]);

  // ============================================
  // 10. Latest Application State
  // ============================================
  // Derive application state from raw serverEvents (not filtered events) to include StateUpdateChatEventNode
  const latestApplicationState = useMemo(() => getLatestApplicationState(serverEvents), [serverEvents]);
  const latestSystemState = useMemo(() => getLatestSystemState(serverEvents), [serverEvents]);
  const hasWriteApproval = useMemo(() => hasChatScopedWriteApproval(serverEvents), [serverEvents]);
  // Folder ID, regardless of agent type - background agents/routines aren't limited to
  // co-scientist. See hasFullAccessForDisplay for why this (rather than hasWriteApproval alone)
  // drives the chip/plus-menu display; callers needing the co-scientist-only UI should additionally
  // check the agent type themselves.
  const rawAgentWorkspaceFolderId = useMemo(
    () => getAgentWorkspaceFolderId(latestApplicationState),
    [latestApplicationState]
  );
  const fullAccessEnabled = useMemo(
    () =>
      hasFullAccessForDisplay(serverEvents, {
        agentWorkspaceFolderId: rawAgentWorkspaceFolderId,
        approvalsEnabled: BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS,
      }),
    [serverEvents, rawAgentWorkspaceFolderId]
  );

  // No manual error handling here: the top-level unhandled-rejection handler already shows an
  // error toast and reports true crashes to Sentry, so a failed grant/revoke can just reject.
  const grantFullAccess = useCallback(
    async (source: AgentApprovalUpdateSource): Promise<boolean> => {
      if (!BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS || chatSessionId == null) {
        return false;
      }
      await updateChatAgentApproval(chatSessionId, true);
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.UPDATED_AGENT_APPROVAL_STATE, {
        chatSessionId,
        source,
        writeActionsEnabled: true,
      });
      await refetch();
      return true;
    },
    [chatSessionId, refetch]
  );

  const revokeFullAccess = useCallback(
    async (source: AgentApprovalUpdateSource): Promise<boolean> => {
      if (!BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS || chatSessionId == null) {
        return false;
      }
      await updateChatAgentApproval(chatSessionId, false);
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.UPDATED_AGENT_APPROVAL_STATE, {
        chatSessionId,
        source,
        writeActionsEnabled: false,
      });
      // Revoking (chip X, or selecting Default) also clears the agent workspace folder
      // server-side (see update_chat_agent_approval) - that's existing behavior this event only
      // observes, not something tracked here separately.
      await refetch();
      return true;
    },
    [chatSessionId, refetch]
  );

  // Lets AI Scientist chats recover a workspace folder after it's been cleared (e.g. by revoking
  // full access) without starting a new chat. Rejects (e.g. on a 409 while the agent is running)
  // rather than swallowing errors - callers should surface the failure to the user.
  const updateAgentWorkspaceFolder = useCallback(
    async (agentWorkspaceFolderId: string): Promise<boolean> => {
      if (!BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS || chatSessionId == null) {
        return false;
      }
      await updateChatAgentWorkspaceFolder(chatSessionId, agentWorkspaceFolderId);
      await refetch();
      return true;
    },
    [chatSessionId, refetch]
  );

  const resolvedErrorMessage = useMemo((): string | null => {
    if (errorMessage != null) {
      return errorMessage;
    }
    if (threadStatus !== ChatThreadStatus.ERROR) {
      return null;
    }
    return latestSystemState?.error_message ?? 'Something went wrong. Please try again.';
  }, [errorMessage, threadStatus, latestSystemState]);

  // ============================================
  // 11. Progress Message (for in-progress or error states)
  // ============================================
  const progressMessage = useMemo((): ClientProgressMessage | null => {
    if (isAgentSubmitErrorMessage) {
      return null;
    }

    const systemState = getLatestSystemState(serverEvents);
    const systemErrorMessage = systemState?.error_message;

    if (systemErrorMessage) {
      const lastDisplayEvent = serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );
      return createProgressMessage({
        status: ProgressStatus.FAILED,
        errorMessage: systemErrorMessage,
        agentType: lastDisplayEvent?.agentType ?? null,
        progressData: currentProgressDataWithInterrupt,
      });
    }

    // Show progress if last message is from user (either pending or persisted), or if the thread is
    // still working after an assistant message. The latter happens when a scheduled wake resumes a
    // completed turn: the continuation runs without a new user display message, so its tool calls
    // follow the previous assistant message.
    const lastDisplayEvent =
      pendingUserMessage ??
      failedUserMessage ??
      serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );

    if (lastDisplayEvent && (lastDisplayEvent.role === LLMRole.USER || isThreadActive)) {
      if (effectiveInterruptProgressPhase === 'stopped') {
        return null;
      }

      // Use IN_PROGRESS when we have tool calls or interrupt is in flight; QUEUED/STARTED otherwise.
      const hasToolCalls =
        currentProgressDataWithInterrupt != null && currentProgressDataWithInterrupt.toolCalls.length > 0;
      const status = getActiveProgressStatus(threadStatus, hasToolCalls, effectiveInterruptProgressPhase);

      return createProgressMessage({
        status,
        errorMessage: null,
        agentType: lastDisplayEvent.agentType,
        progressData: currentProgressDataWithInterrupt,
      });
    }

    return null;
  }, [
    serverEvents,
    pendingUserMessage,
    failedUserMessage,
    threadStatus,
    isThreadActive,
    currentProgressDataWithInterrupt,
    effectiveInterruptProgressPhase,
    isAgentSubmitErrorMessage,
  ]);

  const pendingResponseToolCall = useMemo(
    () => getPendingResponseToolCall(chatSessionQueryData),
    [chatSessionQueryData]
  );
  const optimisticResponseToolMessage = useMemo((): ClientStreamingAssistantMessage | null => {
    const lastDisplayEvent = serverEvents.findLast(
      (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
    );
    if (!lastDisplayEvent || lastDisplayEvent.role !== LLMRole.USER) {
      return null;
    }

    if (!pendingResponseToolCall) {
      return null;
    }

    if (pendingResponseToolCall.answer.length === 0) {
      return null;
    }

    return createStreamingMessage({
      createdAt: pendingResponseToolCall.createdAt,
      agentType: lastDisplayEvent.agentType,
      responseToEventId: lastDisplayEvent.id,
      streamingContent: pendingResponseToolCall.answer,
      displayType: getResponseToolDisplayType(pendingResponseToolCall.toolName),
      progressMessage: getResponseToolProgressMessage(
        pendingResponseToolCall,
        lastDisplayEvent,
        isInterrupting
      ),
    });
  }, [pendingResponseToolCall, serverEvents, isInterrupting]);

  // ============================================
  // 12. Compose Final Events (with completed thinking sections)
  // ============================================
  const events = useMemo<Array<ClientDisplayChatEvent>>(() => {
    const result: Array<ClientDisplayChatEvent> = [];

    // Get the retryOfId from the pending message to filter out the original message being retried
    const retryOfId = pendingUserMessage?.retryOfId ?? failedUserMessage?.retryOfId ?? null;

    let lastDisplayedMessage: DisplayMessageChatEventNode | null = null;

    // Insert completed thinking sections at each completed-exchange boundary.
    for (const event of serverEvents) {
      if (event.__typename === 'InterruptChatEventNode') {
        const thinkingMessage: ClientProgressMessage = createProgressMessage({
          status: ProgressStatus.COMPLETED,
          errorMessage: null,
          agentType: lastDisplayedMessage?.agentType ?? null,
          progressData: withInterruptProgress(
            toolCallsByCompletedProgressBoundary[event.id] ?? null,
            'stopped'
          ),
          associatedMessageId: event.id,
          thinkingDefaultExpanded: true,
        });
        result.push(thinkingMessage);
        continue;
      }

      if (event.__typename !== 'DisplayMessageChatEventNode') {
        continue;
      }

      // Filter out the original message when showing a retry to avoid duplicate display
      if (retryOfId != null && event.id === retryOfId) {
        continue;
      }

      if (event.role === LLMRole.ASSISTANT) {
        const toolCallsForMessage = toolCallsByCompletedProgressBoundary[event.id];
        if (toolCallsForMessage && toolCallsForMessage.toolCalls.length > 0) {
          const thinkingMessage: ClientProgressMessage = createProgressMessage({
            status: ProgressStatus.COMPLETED,
            errorMessage: null,
            agentType: event.agentType,
            progressData: toolCallsForMessage,
            associatedMessageId: event.id,
            thinkingDefaultExpanded: viewerMode ? true : undefined,
          });
          result.push(thinkingMessage);
        }
      }

      result.push(event);
      lastDisplayedMessage = event;
    }

    // Append optimistic user message if present
    if (pendingUserMessage) {
      result.push(pendingUserMessage);
    }
    if (failedUserMessage) {
      result.push(failedUserMessage);
    }

    // Append progress message for in-progress or error states
    if (progressMessage) {
      result.push(progressMessage);
    }

    // Insert streaming message if present (and we don't already have the final message)
    if (optimisticResponseToolMessage ?? streamingMessage) {
      result.push((optimisticResponseToolMessage ?? streamingMessage)!);
    }

    return result;
  }, [
    serverEvents,
    pendingUserMessage,
    failedUserMessage,
    streamingMessage,
    optimisticResponseToolMessage,
    progressMessage,
    toolCallsByCompletedProgressBoundary,
    viewerMode,
  ]);

  // ============================================
  // 13. Last Message From User Detection
  // ============================================
  // New behavior: only an ERROR thread can trigger retry. Interrupted threads complete successfully and
  // must keep their interrupted tool/context events available to the next turn.
  const isLastMessageFromUser = useMemo(() => {
    if (threadStatus !== ChatThreadStatus.ERROR) {
      return false;
    }
    const lastDisplayMessage = serverEvents.findLast(
      (event) => event.__typename === 'DisplayMessageChatEventNode'
    );
    return lastDisplayMessage != null && lastDisplayMessage.role === LLMRole.USER;
  }, [serverEvents, threadStatus]);

  // ============================================
  // 14. Send Message
  // ============================================
  const sendMessage = useCallback(
    (params: SendMessageParams = {}) => {
      const {retry = false} = params;

      assert(pendingUserMessage == null, 'Cannot send message while pending user message is present');

      // Read from draft state - it should already have everything from user input
      const draft = getDraftState(chatSessionId);
      assert(draft != null, 'Cannot send message without draft state');
      assert(draft.settings != null, 'Cannot send message without settings');

      const {agentType} = draft.settings;

      // Capture the most recent DisplayMessageChatEventNode at send time as the replyToId.
      // This is stored in the draft state (not a ref) so it has the same lifecycle as the
      // optimistic message. We use this to detect when the optimistic message has been persisted:
      // once a user message appears after this position, we know the optimistic message was saved.
      const lastDisplayMessage = serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );
      const replyToId = lastDisplayMessage?.id ?? null;

      // Sending a typed reply while an approval request is pending (the latest display message is an
      // unanswered assistant request) implicitly denies it. Record IMPLICITLY_DENIED referencing the
      // request so the decision is persisted on this message rather than inferred from history later.
      const impliedApprovalDenialRequest =
        lastDisplayMessage != null &&
        lastDisplayMessage.role === LLMRole.ASSISTANT &&
        isApprovalRequestMessage(lastDisplayMessage)
          ? lastDisplayMessage
          : null;

      // If this is a retry, capture the ID of the user message being retried so we can filter it out
      const lastUserMessage = retry
        ? serverEvents.findLast(
            (e): e is DisplayMessageChatEventNode =>
              e.__typename === 'DisplayMessageChatEventNode' && e.role === LLMRole.USER
          )
        : null;
      const retryOfId = lastUserMessage?.id ?? null;

      // Start time-to-first-token tracking for ASK
      if (agentType === AgentType.ASK) {
        const perfId = Perf.start('chat.ask.ttft_ms', {enabled: false}) ?? '';
        timeToFirstTokenPerfId.current = perfId;
        Perf.markStart(perfId);
      }

      // Submit the draft with replyToId and retryOfId for optimistic message tracking
      const messageCount = serverEvents.filter((e) => e.__typename === 'DisplayMessageChatEventNode').length;
      ChatActions.submitDraftMessage(chatSessionId, {
        createParams: {
          retry,
          mcpServerIds,
          excludeMcpServers,
          omitSources,
          ...(impliedApprovalDenialRequest != null
            ? {
                agentApprovalDecision: buildAgentApprovalDecision(
                  AgentApprovalResponse.IMPLICITLY_DENIED,
                  impliedApprovalDenialRequest.id,
                  impliedApprovalDenialRequest.agentApprovalRequest.category
                ),
              }
            : {}),
        },
        analytics: {messageCount},
        replyToId,
        retryOfId,
      });

      // A typed reply isn't a card button click, but it's still the user answering the pending
      // request (denying it) - see ANSWERED_AGENT_APPROVAL_REQUEST tracking in AgentApprovalCard for
      // the button-click paths (Allow, Allow once, Deny).
      if (impliedApprovalDenialRequest != null) {
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ANSWERED_AGENT_APPROVAL_REQUEST, {
          chatSessionId,
          category: impliedApprovalDenialRequest.agentApprovalRequest.category,
          response: AgentApprovalResponse.IMPLICITLY_DENIED,
        });
      }
    },
    [chatSessionId, serverEvents, pendingUserMessage, mcpServerIds, excludeMcpServers, omitSources]
  );

  const chatSessionDisplayName = useMemo((): string | null => {
    if (chatSession?.__typename !== 'ChatSessionNode') {
      return null;
    }
    return chatSession.name ?? null;
  }, [chatSession]);

  const messageSenderContext = useMemo(() => {
    if (chatSession?.__typename !== 'ChatSessionNode') {
      return null;
    }
    return getChatMessageSenderContext(chatSession, isRoutineServiceAccountUiEnabled);
  }, [chatSession, isRoutineServiceAccountUiEnabled]);

  const sessionCreatorFallback = useMemo((): MessageCreator | null => {
    if (chatSession?.__typename !== 'ChatSessionNode') {
      return null;
    }
    return chatSession.creator ?? null;
  }, [chatSession]);

  const sidebarData = useMemo((): ChatSidebarData | null => {
    if (chatSessionSidebar?.__typename !== 'ChatSessionNode') {
      return null;
    }
    return {
      chatSessionId: chatSessionSidebar.id,
      affectedObjects: chatSessionSidebar.affectedObjects,
      inputFiles: chatSessionSidebar.inputFiles,
    };
  }, [chatSessionSidebar]);

  const interruptAssistant = useCallback(async (): Promise<void> => {
    if (activeInterruptUserMessageEventId == null || interruptRequestInFlightRef.current) {
      return;
    }
    interruptRequestInFlightRef.current = true;
    setInterruptRequestState(InterruptRequestState.REQUESTING);
    try {
      await ChatActions.interruptChatSession(chatSessionId, activeInterruptUserMessageEventId);
      setInterruptRequestState(InterruptRequestState.REQUESTED);
    } catch (e) {
      setInterruptRequestState(InterruptRequestState.NOT_REQUESTED);
      throw e;
    } finally {
      interruptRequestInFlightRef.current = false;
    }
  }, [activeInterruptUserMessageEventId, chatSessionId]);

  useEffect(() => {
    interruptRequestInFlightRef.current = false;
    setInterruptRequestState(InterruptRequestState.NOT_REQUESTED);
  }, [chatSessionId]);

  useCompleteSkillOnboardingFromChat(progressData?.toolCalls);

  useEffect(() => {
    if (interruptRequestState !== InterruptRequestState.REQUESTED) {
      return;
    }
    if (!isThreadActive || interruptEventsForLastUserMessage.hasInterruptRequestedEvent) {
      setInterruptRequestState(InterruptRequestState.NOT_REQUESTED);
    }
  }, [interruptRequestState, interruptEventsForLastUserMessage, isThreadActive]);

  return {
    events,
    progressData,
    citationData,
    latestApplicationState,
    sidebarData,
    fullAccessEnabled,
    isLoading,
    isAssistantRunning,
    isSubmitErrorMessage: isAgentSubmitErrorMessage,
    isAwaitingUserInput,
    isCompleted,
    errorMessage: resolvedErrorMessage,
    isThreadError: resolvedErrorMessage != null && errorMessage == null,
    isLastMessageFromUser,
    sendMessage,
    canInterruptAssistant,
    isInterrupting,
    interruptAssistant,
    grantFullAccess,
    revokeFullAccess,
    updateAgentWorkspaceFolder,
    refetch,
    chatSessionDisplayName,
    hasChatScopedWriteApproval: hasWriteApproval,
    messageSenderContext,
    sessionCreatorFallback,
    routineOrigin,
  };
}
